import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Instagram, 
  Facebook, 
  Twitter,
  ArrowRight
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const LOGO_URL = "/logo%20viajes.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tripCategories = [
  { name: "Viajes de Familia", slug: "familia" },
  { name: "Viajes de Novios", slug: "novios" },
  { name: "Viajes de Grupos", slug: "grupos" },
  { name: "Cruceros", slug: "cruceros" },
  { name: "Bienestar y Relax", slug: "bienestar" },
  { name: "Arte e Historia", slug: "arte" },
  { name: "Lujo y Compras", slug: "lujo" },
  { name: "Deportivo y Cultural", slug: "deportivo" },
  { name: "Enológicos y Gastronómicos", slug: "enologico" },
  { name: "Naturaleza y Aventura", slug: "naturaleza" },
  { name: "Viajes a Medida", slug: "medida" },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    footer_description: 'Creamos experiencias personalizadas e inolvidables',
    footer_address: 'C./ Cosme Damian Churruca 6, 28660 Boadilla del Monte - Madrid',
    footer_phone: '91 125 63 52 / 678 744 482',
    footer_email: 'reservas@viajesensueno.com'
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings?t=${Date.now()}`);
        console.log('🦶 Footer settings loaded:', res.data);
        if (res.data) {
          setSettings(prev => ({
            ...prev,
            footer_description: res.data.footer_description || prev.footer_description,
            footer_address: res.data.footer_address || prev.footer_address,
            footer_phone: res.data.footer_phone || prev.footer_phone,
            footer_email: res.data.footer_email || prev.footer_email
          }));
        }
      } catch (err) {
        console.error('❌ Error loading footer settings:', err);
      }
    };
    loadSettings();
  }, []);


  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      toast.error('Error al suscribirse. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Newsletter Section - Separate from footer */}
      <section className="bg-primary py-16" data-testid="newsletter-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl md:text-4xl font-serif text-white mb-4">
              Recibe nuestras mejores ofertas
            </h3>
            <p className="text-white/70 mb-8 max-w-2xl mx-auto">
              Suscríbete a nuestra newsletter y sé el primero en conocer las ofertas más exclusivas y descuentos especiales.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Tu email"
                data-testid="newsletter-email"
                className="flex-1 px-6 py-4 rounded-full bg-white/10 border border-white/20 
                         text-white placeholder:text-white/50 focus:outline-none focus:border-accent
                         text-center sm:text-left"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit"
                className="bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full 
                         font-medium transition-all duration-300 flex items-center justify-center gap-2
                         hover:shadow-lg hover:shadow-accent/30"
              >
                {loading ? 'Enviando...' : (
                  <>
                    Suscribirse
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#152d4a] text-white py-16" data-testid="footer">
        <div className="container-custom">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div>
              <img src={LOGO_URL} alt="Viajes de Ensueño" className="h-16 mb-6" />
              <p className="text-white/70 mb-6 whitespace-pre-line">
                {settings.footer_description}
              </p>
              <div className="flex gap-4">
                <a
                  href="https://instagram.com/viajesdeensueno"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://facebook.com/viajesdeensueno"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-facebook"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://twitter.com/viajesdeensueno"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-twitter"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-accent transition-colors"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Tipos de Viajes</h4>
              <ul className="space-y-3">
                {tripCategories.map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/tipos-viajes/${cat.slug}`}
                      className="text-white/70 hover:text-accent transition-colors"
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Empresa</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/viajes" className="text-white/70 hover:text-accent transition-colors">
                    Todos los Viajes
                  </Link>
                </li>
                <li>
                  <Link to="/sobre-nosotros" className="text-white/70 hover:text-accent transition-colors">
                    Sobre Nosotros
                  </Link>
                </li>
                <li>
                  <Link to="/guias-personalizadas" className="text-white/70 hover:text-accent transition-colors">
                    Guías Personalizadas
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-white/70 hover:text-accent transition-colors">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/contacto" className="text-white/70 hover:text-accent transition-colors font-bold text-accent">
                    Planifica tu Viaje
                  </Link>
                </li>
              </ul>

              <h4 className="text-lg font-semibold mb-6 mt-8">Destinos</h4>
              <ul className="space-y-3">
                {['Japón', 'Islandia', 'Maldivas', 'Uzbekistán', 'Albania'].map((name) => (
                  <li key={name}>
                    <Link 
                      to={`/destinos/${name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '-')}`} 
                      className="text-white/70 hover:text-accent transition-colors"
                    >
                      Viajes a {name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Contacto</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <span className="text-white/70">
                    {settings.footer_address}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent flex-shrink-0" />
                  <a href={`tel:${settings.footer_phone.split('/')[0].replace(/\s/g, '').trim()}`} className="text-white/70 hover:text-accent transition-colors">
                    {settings.footer_phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-accent flex-shrink-0" />
                  <a href={`mailto:${settings.footer_email}`} className="text-white/70 hover:text-accent transition-colors">
                    {settings.footer_email}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} Viajes de Ensueño. Todos los derechos reservados.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacidad" className="text-white/50 hover:text-white transition-colors">
                Política de Privacidad
              </Link>
              <Link to="/terminos" className="text-white/50 hover:text-white transition-colors">
                Términos y Condiciones
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
